import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Alert, Button, CircularProgress, TextField } from '@mui/material'
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner'
import KeyboardIcon from '@mui/icons-material/Keyboard'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import { BrowserQRCodeReader } from '@zxing/browser'
import { useNavigate } from 'react-router-dom'
import ReceivingLayout from './ReceivingLayout'

const cameraErrorMessage = error => {
  switch (error?.name) {
    case 'NotAllowedError':
    case 'SecurityError':
      return 'Camera permission was blocked. Allow camera access in your browser settings, then try again.'
    case 'NotFoundError':
    case 'DevicesNotFoundError':
      return 'No camera was found on this device. Upload a QR image or enter the code manually.'
    case 'NotReadableError':
    case 'TrackStartError':
      return 'The camera is being used by another app. Close the other app, then try again.'
    case 'OverconstrainedError':
    case 'ConstraintNotSatisfiedError':
      return 'The rear camera could not be started. Try again or upload a QR image.'
    default:
      return 'The camera could not be started. Try again, upload a QR image, or enter the code manually.'
  }
}

export default function ReceivingHome() {
  const navigate = useNavigate()
  const videoRef = useRef(null)
  const controlsRef = useRef(null)
  const uploadRef = useRef(null)
  const [code, setCode] = useState('')
  const [scanning, setScanning] = useState(false)
  const [cameraReady, setCameraReady] = useState(false)
  const [cameraError, setCameraError] = useState('')
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState('')

  const releaseCamera = useCallback(() => {
    controlsRef.current?.stop()
    controlsRef.current = null
    const video = videoRef.current
    if (video?.srcObject) {
      video.srcObject.getTracks().forEach(track => track.stop())
      video.srcObject = null
    }
  }, [])

  const stopCamera = useCallback(() => {
    releaseCamera()
    setScanning(false)
    setCameraReady(false)
  }, [releaseCamera])

  const openCode = useCallback(value => {
    const cleaned = value.trim()
    if (!cleaned) return
    stopCamera()
    navigate(`/receiving/package/${encodeURIComponent(cleaned)}`)
  }, [navigate, stopCamera])

  useEffect(() => () => releaseCamera(), [releaseCamera])

  useEffect(() => {
    if (!scanning) return undefined

    let cancelled = false
    let scannerControls = null

    const startCamera = async () => {
      try {
        const video = videoRef.current
        if (!video) throw new Error('Camera preview is not mounted.')

        const reader = new BrowserQRCodeReader()
        scannerControls = await reader.decodeFromConstraints({
          audio: false,
          video: {
            facingMode: { ideal: 'environment' },
            width: { ideal: 1280 },
            height: { ideal: 720 }
          }
        }, video, result => {
          if (result) openCode(result.getText())
        })

        if (cancelled) {
          scannerControls.stop()
          return
        }
        controlsRef.current = scannerControls
      } catch (error) {
        if (cancelled) return
        console.error('Unable to start receiving camera', error)
        releaseCamera()
        setScanning(false)
        setCameraReady(false)
        setCameraError(cameraErrorMessage(error))
      }
    }

    startCamera()
    return () => {
      cancelled = true
      scannerControls?.stop()
      if (controlsRef.current === scannerControls) controlsRef.current = null
    }
  }, [openCode, releaseCamera, scanning])

  const scan = () => {
    setCameraError('')
    setCameraReady(false)
    if (window.isSecureContext === false || !navigator.mediaDevices?.getUserMedia) {
      setCameraError('Camera scanning requires HTTPS and camera permission. Upload a QR image or enter the code manually.')
      return
    }
    setScanning(true)
  }

  const uploadQr = async event => {
    const file = event.target.files?.[0]
    if (!file) return
    setUploadError('')
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      setUploadError('Choose a JPG, PNG, or WebP image.')
      event.target.value = ''
      return
    }
    if (file.size > 10 * 1024 * 1024) {
      setUploadError('The QR image must be 10 MB or smaller.')
      event.target.value = ''
      return
    }
    stopCamera()
    setUploading(true)
    const imageUrl = URL.createObjectURL(file)
    try {
      const result = await new BrowserQRCodeReader().decodeFromImageUrl(imageUrl)
      openCode(result.getText())
    } catch (error) {
      setUploadError('No readable QR code was found. Try a clearer image with the complete QR code visible.')
    } finally {
      URL.revokeObjectURL(imageUrl)
      setUploading(false)
      if (uploadRef.current) uploadRef.current.value = ''
    }
  }

  return <ReceivingLayout title="Scan a package label">
    <section className="receiving-card">
      <div className="receiving-icon"><QrCodeScannerIcon /></div>
      <h2>Scan QR code</h2>
      <p>Point the rear camera at the printed AIMS label. Scanning requires HTTPS on a mobile device.</p>
      {cameraError && <Alert severity="warning" role="alert">{cameraError}</Alert>}
      {scanning ? <>
        <div className={`receiving-video-wrap${cameraReady ? ' is-ready' : ''}`}>
          <video
            ref={videoRef}
            autoPlay
            muted
            playsInline
            aria-label="QR camera preview"
            onLoadedData={() => setCameraReady(true)}
            onPlaying={() => setCameraReady(true)}
          />
          {!cameraReady && <div className="receiving-camera-starting" role="status" aria-live="polite">
            <CircularProgress size={32} color="inherit" />
            <span>Starting camera…</span>
          </div>}
        </div>
        <Button fullWidth variant="outlined" onClick={stopCamera}>Stop camera</Button>
      </> : <Button className="receiving-primary" fullWidth variant="contained" startIcon={<QrCodeScannerIcon />} onClick={scan}>Open camera scanner</Button>}
      <div className="receiving-divider"><span>or</span></div>
      {uploadError && <Alert severity="warning" role="alert">{uploadError}</Alert>}
      <Button fullWidth variant="outlined" component="label" startIcon={<UploadFileIcon />} disabled={uploading}>
        {uploading ? 'Reading QR image…' : 'Upload QR image'}
        <input ref={uploadRef} className="receiving-file-input" type="file" accept="image/jpeg,image/png,image/webp" onChange={uploadQr} />
      </Button>
      <p className="receiving-upload-help">Choose a photo or screenshot containing one complete QR code.</p>
    </section>
    <section className="receiving-card">
      <div className="receiving-icon secondary"><KeyboardIcon /></div>
      <h2>Enter code manually</h2>
      <form onSubmit={event => { event.preventDefault(); openCode(code) }}>
        <TextField label="Label code" helperText="Example: HK-000000001" fullWidth required value={code} onChange={event => setCode(event.target.value.toUpperCase())} inputProps={{ autoCapitalize: 'characters', style: { fontSize: 16 } }} />
        <Button fullWidth variant="outlined" type="submit" disabled={!code.trim()}>Look up label</Button>
      </form>
    </section>
  </ReceivingLayout>
}
